import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import PaymentReviewModal from './PaymentReviewModal';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { dashboardService, User } from '../../services/dashboardService';
import { transactionOperationsService } from '../../services/transactionOperationsService';
import { walletService, WalletBalance } from '../../services/walletService';
import { asArray } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';

interface TransactionFees {
  superAgentPercent: number;
  agentPercent: number;
  superAgent: number;
  agent: number;
  netPayout: number;
}

interface Payout {
  id: number;
  transactionId: number;
  type: 'Admin' | 'Agent' | 'SuperAgent';
  toWallet: string;
  amount: string;
  createtAt: string;
  createtBy: string;
  txHash: string;
  status: string;
}

interface CheckoutSession {
  id: number;
  transactionId: number;
  paymentMethod: string;
  url: string;
  status: string;
  createdAt: string;
  expireAt: string;
}

interface Transaction {
  id: number;
  userId: number;
  provider: string;
  fiatCurrency: string;
  fiatAmount: number;
  cryptoCurrency: string;
  cryptoAmount: number;
  userWallet: string;
  uniqueWalletAddress: string;
  status: 'Issued' | 'Completed' | 'Pending' | 'Failed' | 'PayoutCompleted';
  timestamp: string;
  txHash?: string;
  fees: TransactionFees;
  payouts?: Payout[];
  checkoutSessions?: CheckoutSession[];
}

interface TransactionFilters {
  status: string;
  dateFrom: string;
  dateTo: string;
  userId: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

const BRAND = '#2297D1';

const SurfaceCard: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <section
    className={cn(
      'rounded-[30px] border border-[#d1d9e0] bg-[#F6F6F6] p-[30px] shadow-[0_2px_8px_rgba(15,23,42,0.06),0_1px_2px_rgba(15,23,42,0.04)]',
      className
    )}
  >
    {children}
  </section>
);

type TxMetricAccent = 'blue' | 'amber' | 'green' | 'sky';

const txMetricStyles: Record<TxMetricAccent, { leftColor: string; label: string }> = {
  blue: { leftColor: '#2297D1', label: 'text-[#2297D1]' },
  amber: { leftColor: '#f59e0b', label: 'text-amber-600' },
  green: { leftColor: '#059669', label: 'text-emerald-700' },
  sky: { leftColor: '#0ea5e9', label: 'text-sky-600' },
};

const TxMetricTile: React.FC<{
  label: string;
  value: string | number;
  hint: string;
  accent: TxMetricAccent;
}> = ({ label, value, hint, accent }) => {
  const s = txMetricStyles[accent];
  return (
    <div className='rounded-[20px] overflow-hidden'>
      <div
      className=" border-l border-[#dce3ea] bg-white py-2 px-[22px] shadow-lg h-full"
      style={{ borderLeft: `6px solid ${s.leftColor}` }}
    >
      <p className={cn('text-[10px] sm:text-base font-semibold uppercase tracking-[0.16em]', s.label)}>{label}</p>
      <p className="mt-2 text-2xl font-bold tracking-tight text-black sm:text-3xl">{value}</p>
      <p className="mt-1.5 text-sm leading-snug text-slate-500">{hint}</p>
    </div>
    </div>
  );
};

const inputClass =
  'w-full rounded-xl border border-[#66666685] bg-[#F0F0F0] p-[12px] text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25';

const labelClass = 'mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500';

const TransactionList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TransactionFilters>({
    status: '',
    dateFrom: '',
    dateTo: '',
    userId: '',
  });
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const pageSize = 10;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showUserDropdown, setShowUserDropdown] = useState<boolean>(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showTransactionModal, setShowTransactionModal] = useState<boolean>(false);
  const [walletBalances, setWalletBalances] = useState<{ [address: string]: WalletBalance }>({});
  const [loadingBalances, setLoadingBalances] = useState<boolean>(false);
  const [manualSettlingId, setManualSettlingId] = useState<number | null>(null);
  const [expandedSections, setExpandedSections] = useState<{
    generatedLinks: boolean;
    wallets: boolean;
  }>({
    generatedLinks: false,
    wallets: false,
  });

  const [referenceSearch, setReferenceSearch] = useState('');
  const [needsAttention, setNeedsAttention] = useState('');
  const [appliedReference, setAppliedReference] = useState('');
  const [appliedNeedsAttention, setAppliedNeedsAttention] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [reviewStateAdv, setReviewStateAdv] = useState('');
  const [followUpAdv, setFollowUpAdv] = useState('');
  const [ownersAdv, setOwnersAdv] = useState('');
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [browserAlertEnabled, setBrowserAlertEnabled] = useState(
    () => localStorage.getItem('zkGate.browserAlerts') === '1'
  );
  const [paymentToneOn, setPaymentToneOn] = useState(
    () => localStorage.getItem('zkGate.paymentTone') !== '0'
  );
  const [lastChecked, setLastChecked] = useState<string>('');

  const userRole = getCurrentUserRole() as 'Admin' | 'SuperAgent' | 'Agent' | null;
  const userId = getCurrentUserId();

  const fetchUsers = useCallback(async () => {
    try {
      const usersData = await dashboardService.getUsers();
      setUsers(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString();
  };

  const fetchTransactions = useCallback(
    async (page: number = 1) => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('accessToken');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());

        // "Settled" is a UI label — API enum uses "Completed" (and PayoutCompleted server-side).
        const apiStatus = filters.status === 'Settled' ? 'Completed' : filters.status;
        if (apiStatus) params.append('status', apiStatus);
        if (filters.dateFrom) {
          params.append('dateFrom', formatDateForAPI(filters.dateFrom));
        }
        if (filters.dateTo) {
          params.append('dateTo', formatDateForAPI(filters.dateTo));
        }
        if (filters.userId) params.append('userId', filters.userId);

        const response = await fetch(`${API_ENDPOINTS.TRANSACTIONS}?${params.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          console.error('Error response:', errorData);
          let errorMessage = `HTTP error! status: ${response.status}`;
          if (errorData?.expandedError?.message) {
            errorMessage = errorData.expandedError.message;
          } else if (errorData?.message) {
            errorMessage = errorData.message;
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        const transactionArray = asArray<Transaction>(data);
        let totalItems = 0;
        let totalPages = 0;

        if (data && typeof data === 'object' && !Array.isArray(data)) {
          const o = data as Record<string, unknown>;
          const p = o.pagination as { totalItems?: number; total?: number; totalPages?: number } | undefined;
          if (p) {
            totalItems = p.totalItems ?? p.total ?? 0;
            totalPages = p.totalPages ?? (Math.ceil(totalItems / pageSize) || 1);
          } else if (typeof o.total === 'number') {
            totalItems = o.total;
            totalPages = Math.ceil(totalItems / pageSize);
          }
        }
        if (!totalPages) {
          const hasMorePages = transactionArray.length === pageSize;
          totalItems = hasMorePages
            ? page * pageSize + 1
            : (page - 1) * pageSize + transactionArray.length;
          totalPages = hasMorePages ? page + 1 : page;
        }

        const sortedTransactions = transactionArray.sort((a, b) => {
          const dateA = new Date(a.timestamp).getTime();
          const dateB = new Date(b.timestamp).getTime();
          return dateB - dateA;
        });

        setTransactions(sortedTransactions);
        setPagination((prev) => ({
          ...prev,
          currentPage: page,
          totalPages,
          totalItems,
          itemsPerPage: pageSize,
        }));
        setLastChecked(new Date().toLocaleTimeString());
        return sortedTransactions;
      } catch (err) {
        console.error('Error fetching transactions:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch transactions');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  const fetchTransactionById = useCallback(async (id: number): Promise<Transaction | null> => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return null;
      const response = await fetch(`${API_ENDPOINTS.TRANSACTIONS}/${id}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (data && typeof data === 'object' && 'id' in data) {
        return data as Transaction;
      }
      if (data?.data && typeof data.data === 'object' && 'id' in data.data) {
        return data.data as Transaction;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (userRole === 'Admin' || userRole === 'SuperAgent') {
      fetchUsers();
    }
  }, [userRole, fetchUsers]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getFilteredUsers = (): User[] => {
    let filteredUsers: User[] = [];
    if (userRole === 'Admin') {
      filteredUsers = users;
    } else if (userRole === 'SuperAgent') {
      filteredUsers = users.filter(
        (user) =>
          user.id === parseInt(userId || '0', 10) ||
          (user.role === 'Agent' && user.id !== parseInt(userId || '0', 10))
      );
    } else {
      filteredUsers = users.filter((user) => user.id === parseInt(userId || '0', 10));
    }
    if (searchTerm.trim()) {
      filteredUsers = filteredUsers.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filteredUsers;
  };

  const getSelectedUserDisplay = (): string => {
    const user = users.find((u) => u.id === parseInt(filters.userId, 10));
    if (user) {
      return `${user.name} (${user.role})`;
    }
    return 'All accounts';
  };

  const handleUserSelect = (uid: string) => {
    setFilters((prev) => ({ ...prev, userId: uid }));
    setSearchTerm('');
    setShowUserDropdown(false);
  };

  const handleFilterChange = (field: keyof TransactionFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const matchesNeedsAttentionFilter = (t: Transaction, filter: string): boolean => {
    if (!filter) return true;
    if (filter === 'attention') {
      return t.status === 'Pending' || t.status === 'Failed';
    }
    if (filter === 'Failed' || filter === 'Expired') {
      return t.status === 'Failed';
    }
    if (filter === 'Overpaid') {
      return t.status === 'Completed';
    }
    if (
      filter === 'Late Quote' ||
      filter === 'Delivery Retrying' ||
      filter === 'Delivery Failed'
    ) {
      return t.status === 'Pending' || t.status === 'Failed';
    }
    return true;
  };

  const playAttentionTone = () => {
    if (!paymentToneOn) return;
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.value = 0.08;
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } catch {
      /* audio optional */
    }
  };

  const maybeNotifyAttention = (attentionCount: number) => {
    if (!browserAlertEnabled || attentionCount <= 0) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    new Notification('ZK Gate — payments need attention', {
      body: `${attentionCount} payment(s) on this page need follow-up.`,
      tag: 'zk-gate-tx-attention',
    });
  };

  const handleApplyFilters = async () => {
    const nextReference = referenceSearch.trim();
    setAppliedReference(nextReference);
    setAppliedNeedsAttention(needsAttention);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    const loaded = (await fetchTransactions(1)) ?? [];
    let visible = loaded;
    if (needsAttention) {
      visible = visible.filter((t) => matchesNeedsAttentionFilter(t, needsAttention));
    }
    if (nextReference) {
      const q = nextReference.toLowerCase();
      visible = visible.filter((t) => {
        const name = getUsernameById(t.userId).toLowerCase();
        return (
          String(t.id).includes(q) ||
          (t.uniqueWalletAddress || '').toLowerCase().includes(q) ||
          (t.userWallet || '').toLowerCase().includes(q) ||
          name.includes(q) ||
          String(t.userId).includes(q) ||
          (t.provider || '').toLowerCase().includes(q)
        );
      });
    }
    const attentionCount = visible.filter(
      (t) => t.status === 'Pending' || t.status === 'Failed'
    ).length;
    maybeNotifyAttention(attentionCount);
    if (attentionCount > 0) playAttentionTone();
  };

  const handleClearFilters = () => {
    setFilters({ status: '', dateFrom: '', dateTo: '', userId: '' });
    setReferenceSearch('');
    setNeedsAttention('');
    setAppliedReference('');
    setAppliedNeedsAttention('');
    setReviewStateAdv('');
    setFollowUpAdv('');
    setOwnersAdv('');
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchTransactions(1);
  };

  const handlePageChange = (page: number) => {
    fetchTransactions(page);
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

  const formatAmount = (amount: number, currency: string) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  const truncateAddress = (address: string) =>
    address.length > 12 ? `${address.slice(0, 8)}…${address.slice(-6)}` : address;

  const getUsernameById = useCallback(
    (uid: number): string => {
      if (userRole === 'Agent') {
        return `User ${uid}`;
      }
      if (users.length === 0) {
        return 'Loading...';
      }
      const user = users.find((u) => u.id === uid);
      return user ? user.name : `User ${uid}`;
    },
    [users, userRole]
  );

  const getUserById = (uid: number): User | undefined => users.find((u) => u.id === uid);

  const getSuperAgentForAgent = (agentId: number): User | undefined => {
    const agent = users.find((u) => u.id === agentId);
    if (agent?.createdById) {
      return users.find((u) => u.id === agent.createdById);
    }
    return undefined;
  };

  const toggleSection = (section: 'generatedLinks' | 'wallets') => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const fetchWalletBalances = async (addresses: string[]) => {
    setLoadingBalances(true);
    try {
      const balancePromises = addresses.map(async (address) => {
        try {
          const balance = await walletService.getWalletBalance(address);
          return { address, balance };
        } catch (e) {
          console.error(`Error fetching balance for ${address}:`, e);
          return { address, balance: { address, usdtBalance: 0, maticBalance: 0 } };
        }
      });
      const results = await Promise.all(balancePromises);
      const balanceMap: { [address: string]: WalletBalance } = {};
      results.forEach(({ address, balance }) => {
        balanceMap[address] = balance;
      });
      setWalletBalances(balanceMap);
    } catch (e) {
      console.error('Error fetching wallet balances:', e);
    } finally {
      setLoadingBalances(false);
    }
  };

  const handleTransactionClick = async (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
    if (userRole !== 'Agent') {
      await fetchWalletBalances([transaction.uniqueWalletAddress]);
    }
  };

  const handleCloseModal = () => {
    setShowTransactionModal(false);
    setSelectedTransaction(null);
  };

  const handleManualSettlement = async (
    transactionId: number,
    payload: { cryptoAmount: number; cryptoCurrency: string; fundingTxHash?: string; note?: string }
  ) => {
    setManualSettlingId(transactionId);
    try {
      await transactionOperationsService.completeManualSettlement(transactionId, payload);
      const refreshed = await fetchTransactionById(transactionId);
      if (refreshed) {
        setSelectedTransaction(refreshed);
      }
      await fetchTransactions(pagination.currentPage);
    } finally {
      setManualSettlingId(null);
    }
  };

  const openingFromRouteRef = useRef(false);

  useEffect(() => {
    const st = (location.state || null) as {
      transactionsQueue?: string;
      openTransactionId?: number;
    } | null;
    if (!st || st.transactionsQueue !== 'attention') return;

    setNeedsAttention('attention');
    setAppliedNeedsAttention('attention');
    setPagination((prev) => ({ ...prev, currentPage: 1 }));

    const next: { openTransactionId?: number } = {};
    if (st.openTransactionId != null) next.openTransactionId = st.openTransactionId;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: Object.keys(next).length ? next : {} });
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    const openId = (location.state as { openTransactionId?: number } | null)?.openTransactionId;
    if (openId == null || loading || openingFromRouteRef.current) {
      return;
    }

    openingFromRouteRef.current = true;

    const run = async () => {
      try {
        let tx = transactions.find((t) => t.id === openId) ?? null;
        if (!tx) {
          tx = await fetchTransactionById(openId);
        }
        navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
        if (tx) {
          await handleTransactionClick(tx);
        }
      } finally {
        openingFromRouteRef.current = false;
      }
    };

    void run();
  }, [loading, transactions, location.state, location.pathname, location.search, navigate, fetchTransactionById]);

  const visibleTransactions = useMemo(() => {
    let list = transactions;
    if (appliedNeedsAttention) {
      list = list.filter((t) => matchesNeedsAttentionFilter(t, appliedNeedsAttention));
    }
    if (appliedReference) {
      const q = appliedReference.toLowerCase();
      list = list.filter((t) => {
        const name = getUsernameById(t.userId).toLowerCase();
        return (
          String(t.id).includes(q) ||
          (t.uniqueWalletAddress || '').toLowerCase().includes(q) ||
          (t.userWallet || '').toLowerCase().includes(q) ||
          name.includes(q) ||
          String(t.userId).includes(q) ||
          (t.provider || '').toLowerCase().includes(q)
        );
      });
    }
    return list;
  }, [transactions, appliedNeedsAttention, appliedReference, getUsernameById]);

  const needsAttentionCount = useMemo(
    () => visibleTransactions.filter((t) => t.status === 'Pending' || t.status === 'Failed').length,
    [visibleTransactions]
  );
  const settledCount = useMemo(
    () => visibleTransactions.filter((t) => t.status === 'Completed' || t.status === 'PayoutCompleted').length,
    [visibleTransactions]
  );

  const filtersAppliedSummary = useMemo(() => {
    const parts: string[] = [];
    if (filters.status) parts.push(`Status: ${filters.status}`);
    if (filters.dateFrom || filters.dateTo) parts.push('Date range');
    if (filters.userId) parts.push('Account');
    if (appliedNeedsAttention === 'attention') parts.push('Needs attention');
    if (appliedReference) parts.push('Search');
    if (reviewStateAdv) parts.push('Review');
    if (followUpAdv) parts.push('Follow-up');
    if (ownersAdv) parts.push('Owners');
    return parts.length ? parts.join(' · ') : 'Showing everything';
  }, [filters, appliedNeedsAttention, appliedReference, reviewStateAdv, followUpAdv, ownersAdv]);

  const hasPendingFilterUi =
    !!filters.status ||
    !!filters.dateFrom ||
    !!filters.dateTo ||
    !!filters.userId ||
    !!referenceSearch.trim() ||
    !!needsAttention ||
    !!reviewStateAdv ||
    !!followUpAdv ||
    !!ownersAdv;

  const toggleRowSelected = (id: number, e: React.SyntheticEvent) => {
    e.stopPropagation();
    setSelectedRowIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const exportFilteredCsv = () => {
    const rows = visibleTransactions;
    const header = ['id', 'userId', 'userName', 'status', 'fiatAmount', 'fiatCurrency', 'cryptoAmount', 'cryptoCurrency', 'provider', 'timestamp'];
    const lines = [
      header.join(','),
      ...rows.map((t) =>
        [
          t.id,
          t.userId,
          `"${getUsernameById(t.userId).replace(/"/g, '""')}"`,
          t.status,
          t.fiatAmount,
          t.fiatCurrency,
          t.cryptoAmount,
          t.cryptoCurrency,
          `"${(t.provider || '').replace(/"/g, '""')}"`,
          `"${t.timestamp}"`,
        ].join(',')
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-page-${pagination.currentPage}.csv`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const statusPillClass = (status: Transaction['status']) => {
    if (status === 'Completed' || status === 'PayoutCompleted') return 'bg-sky-100 text-sky-900 ring-1 ring-sky-200';
    if (status === 'Pending') return 'bg-amber-100 text-amber-900 ring-1 ring-amber-200';
    if (status === 'Issued') return 'bg-blue-100 text-blue-900 ring-1 ring-blue-200';
    return 'bg-rose-100 text-rose-900 ring-1 ring-rose-200';
  };

  const statusHeadline = (status: Transaction['status']) => {
    if (status === 'Completed' || status === 'PayoutCompleted') return 'SETTLED';
    if (status === 'Failed') return 'NEEDS REVIEW';
    return status.toUpperCase();
  };

  const reviewLabel = (status: Transaction['status']) =>
    status === 'Completed' || status === 'PayoutCompleted' ? 'CLOSED' : 'OPEN';

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;
    const pages: number[] = [];
    const startPage = Math.max(1, pagination.currentPage - 2);
    const endPage = Math.min(pagination.totalPages, pagination.currentPage + 2);
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    const btn =
      'rounded-lg border border-[#d2d8dd] px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-40';
    return (
      <div className="flex flex-wrap items-center justify-center gap-2 py-2">
        <button
          type="button"
          className={cn(btn, 'bg-white text-slate-700 hover:bg-slate-50')}
          disabled={pagination.currentPage === 1}
          onClick={() => handlePageChange(1)}
        >
          First
        </button>
        <button
          type="button"
          className={cn(btn, 'bg-white text-slate-700 hover:bg-slate-50')}
          disabled={pagination.currentPage === 1}
          onClick={() => handlePageChange(pagination.currentPage - 1)}
        >
          Prev
        </button>
        {pages.map((i) => (
          <button
            key={i}
            type="button"
            className={cn(
              btn,
              i === pagination.currentPage ? 'border-[#2297D1] bg-[#2297D1] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
            )}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        ))}
        <button
          type="button"
          className={cn(btn, 'bg-white text-slate-700 hover:bg-slate-50')}
          disabled={pagination.currentPage === pagination.totalPages}
          onClick={() => handlePageChange(pagination.currentPage + 1)}
        >
          Next
        </button>
        <button
          type="button"
          className={cn(btn, 'bg-white text-slate-700 hover:bg-slate-50')}
          disabled={pagination.currentPage === pagination.totalPages}
          onClick={() => handlePageChange(pagination.totalPages)}
        >
          Last
        </button>
      </div>
    );
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="min-h-screen bg-slate-100 px-3 py-8 sm:px-4">
        <div className="mx-auto flex max-w-[1440px] items-center justify-center py-24">
          <Spinner animation="border" role="status" style={{ color: BRAND }}>
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-3 py-3 sm:px-6 sm:py-4 homeBg">
      <div className="mx-auto w-full max-w-[1440px] space-y-[40px] pt-3">
        {/* Header */}
        <SurfaceCard className="border border-[#d1d9e0] blurLayerInteractiveInteractive p-4 shadow-lg">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="mb-1 text-base uppercase text-[#4A4A4A]">Payment workspace</p>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Transactions</h1>
              <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate-600">
                Review payment activity, find exceptions quickly, and keep follow-up work moving without digging through raw tables.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                title="When on, requests browser permission and shows a desktop notification if payments need follow-up after you apply filters."
                onClick={async () => {
                  const next = !browserAlertEnabled;
                  if (next && typeof window !== 'undefined' && 'Notification' in window) {
                    if (Notification.permission === 'default') {
                      await Notification.requestPermission();
                    }
                  }
                  setBrowserAlertEnabled(next);
                  localStorage.setItem('zkGate.browserAlerts', next ? '1' : '0');
                }}
                className={cn(
                  'border-[1px] p-[16px] rounded-4 text-base font-semibold transition-colors',
                  browserAlertEnabled
                    ? 'border-[#2297D1] text-gray-500 hover:bg-gray-50'
                    : 'border-[#d2d8dd] text-gray-500 hover:bg-gray-50'
                )}
              >
                {browserAlertEnabled ? 'Browser alert on' : 'Enable browser alert'}
              </button>
              <button
                type="button"
                onClick={exportFilteredCsv}
                className="border-[1px] border-[#2297D1] text-[#2297D1] p-[16px] rounded-4 text-base font-semibold transition-colors"
              >
                Export filtered CSV
              </button>
              <button
                type="button"
                title="When on, plays a short tone after filters are applied if any payment on this page needs attention."
                onClick={() => {
                  const next = !paymentToneOn;
                  setPaymentToneOn(next);
                  localStorage.setItem('zkGate.paymentTone', next ? '1' : '0');
                }}
                className={cn(
                  'border-[1px] p-[16px] rounded-4 text-base font-semibold transition-colors',
                  paymentToneOn
                    ? 'border-[#198754] bg-emerald-50/50 text-[#198754] hover:bg-emerald-50'
                    : 'border-[#d2d8dd] text-gray-500 hover:bg-gray-50'
                )}
              >
                {paymentToneOn ? 'Payment tone on' : 'Payment tone off'}
              </button>
            </div>
          </div>
          <div className="mt-4 grid gap-4   sm:grid-cols-3">
            <div className="rounded-3 bg-[#F0F0F0] p-3">
              <p className="text-[10px] sm:text-sm font-semibold uppercase  text-slate-500">Refresh</p>
              <p className="mt-0.5 text-sm sm:text-base text-slate-700">
                Live every 15 seconds — Last checked {lastChecked || '—'}
              </p>
            </div>
            <div className="rounded-3 bg-[#F0F0F0] p-3">
              <p className="text-[10px] sm:text-sm font-semibold uppercase  text-slate-500">Visible scope</p>
              <p className="mt-0.5 text-sm sm:text-base font-semibold text-slate-900">
                {visibleTransactions.length} matching payments
                {pagination.totalItems > 0 && (
                  <span className="ml-1 font-normal text-slate-500">({pagination.totalItems} total)</span>
                )}
              </p>
            </div>
            <div className="rounded-3 bg-[#F0F0F0] p-3">
              <p className="text-[10px] sm:text-sm font-semibold uppercase  text-slate-500">Active filters</p>
              <p className="mt-0.5 text-sm sm:text-base text-slate-700">{filtersAppliedSummary}</p>
            </div>
          </div>
        </SurfaceCard>

        {/* Summary metrics */}
        <div className="grid grid-cols-2 lg:gap-8 gap-3 md:gap-4 lg:grid-cols-4">
          <TxMetricTile
            label="This page"
            value={visibleTransactions.length}
            hint="Payments loaded in the current view."
            accent="blue"
          />
          <TxMetricTile
            label="Needs attention"
            value={needsAttentionCount}
            hint="Exceptions, overdue follow-up, or escalated reviews."
            accent="amber"
          />
          <TxMetricTile
            label="Settled"
            value={settledCount}
            hint="Payments already completed or fully settled."
            accent="green"
          />
          <TxMetricTile
            label="Selected"
            value={selectedRowIds.length}
            hint="Ready for bulk review updates from this page."
            accent="sky"
          />
        </div>

        {/* Filters */}
        <SurfaceCard className=" !p-0 shadow-lg">
          <div className="mb-3 flex flex-col gap-1.5 p-[30px] sm:flex-row sm:items-center sm:justify-between border-b-2">
            <div>
              <h2 className="text-base lg:text-2xl font-semibold text-slate-900">Find the payments you need</h2>
              <p className="text-[11px] sm:text-base mt-2 text-slate-500">Filter by stage, account, and references — same server filters as before.</p>
            </div>
            <p className="text-[11px] sm:text-sm font-medium text-slate-500">{hasPendingFilterUi ? 'Filters in progress' : 'No filters applied'}</p>
          </div>
          

          <div className="grid gap-3 lg:grid-cols-4 p-[30px]">
            <div>
              <label className={labelClass} htmlFor="tx-status">
                Status
              </label>
              <select
                id="tx-status"
                className={inputClass}
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All payment stages</option>
                <option value="Issued">Issued</option>
                <option value="Completed">Completed</option>
                <option value="Pending">Pending</option>
                <option value="Failed">Failed</option>
                <option value="Settled">Settled</option>
              </select>
              <p className="mt-1 text-xs text-slate-500">Matches the API status filter on refresh.</p>
            </div>
            <div>
              <label className={labelClass} htmlFor="tx-attention">
                Needs attention
              </label>
              <select
                id="tx-attention"
                className={inputClass}
                value={needsAttention}
                onChange={(e) => setNeedsAttention(e.target.value)}
              >
                <option value="">Any issue</option>
                <option value="attention">Underpaid / needs follow-up</option>
                <option value="Overpaid">Overpaid</option>
                <option value="Late Quote">Late Quote</option>
                <option value="Failed">Failed</option>
                <option value="Expired">Expired</option>
                <option value="Delivery Retrying">Delivery Retrying</option>
                <option value="Delivery Failed">Delivery Failed</option>
              </select>
              <p className="mt-1 text-xs text-slate-500">Refines the current page after load (apply to refresh).</p>
            </div>
            <div>
              <label className={labelClass} htmlFor="tx-ref">
                Search references
              </label>
              <input
                id="tx-ref"
                type="search"
                className={inputClass}
                placeholder="Reference ID, wallet, provider, or account name"
                value={referenceSearch}
                onChange={(e) => setReferenceSearch(e.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500">Client-side match on the loaded page (apply to refresh list).</p>
            </div>
            {(userRole === 'Admin' || userRole === 'SuperAgent') && (
              <div className="relative" ref={userDropdownRef}>
                <label className={labelClass}>Account</label>
                <div className="relative">
                  <input
                    type="text"
                    className={cn(inputClass, 'pr-14')}
                    placeholder="Find an account"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowUserDropdown(true);
                    }}
                    onFocus={() => setShowUserDropdown(true)}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#2297D1]"
                    onClick={() => setShowUserDropdown((s) => !s)}
                  >
                    {showUserDropdown ? 'Hide' : 'Show'}
                  </button>
                </div>
                {showUserDropdown && (
                  <div className="absolute z-30 mt-1 max-h-52 w-full overflow-y-auto rounded-xl border border-[#d9dde2] bg-white shadow-lg">
                    <button
                      type="button"
                      className={cn(
                        'w-full border-b border-[#eef1f4] px-3 py-2.5 text-left text-sm',
                        filters.userId === '' ? 'bg-[#2297D1] text-white' : 'text-slate-700 hover:bg-slate-50'
                      )}
                      onClick={() => handleUserSelect('')}
                    >
                      All accounts
                    </button>
                    {getFilteredUsers().map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className={cn(
                          'w-full border-b border-[#eef1f4] px-3 py-2.5 text-left text-sm',
                          filters.userId === user.id.toString() ? 'bg-[#2297D1] text-white' : 'text-slate-700 hover:bg-slate-50'
                        )}
                        onClick={() => handleUserSelect(user.id.toString())}
                      >
                        <span className="font-semibold">{user.name}</span>
                        <span className={cn('block text-xs', filters.userId === user.id.toString() ? 'text-white/90' : 'text-slate-500')}>
                          {user.email} · {user.role}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {!!filters.userId && (
                  <p className="mt-1 text-xs text-slate-600">
                    Selected: <span className="font-semibold text-slate-900">{getSelectedUserDisplay()}</span>
                  </p>
                )}
                <p className="mt-1 text-xs text-slate-500">Sent as userId to the transactions API when you apply.</p>
              </div>
            )}
            {userRole === 'Agent' && (
              <div>
                <label className={labelClass}>Account</label>
                <div className={cn(inputClass, 'text-slate-600')}>Your workspace (agent scope)</div>
                <p className="mt-1 text-xs text-slate-500">Account filter is managed by your login scope.</p>
              </div>
            )}
          </div>

          <div className="  flex flex-col gap-2 px-[30px] pb-[30px]  sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleApplyFilters}
                className="  bg-[#2297D1] px-[18px] py-2 rounded-3 text-sm font-semibold text-white shadow-sm hover:bg-[#1b7fb3]"
              >
                Apply filters
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                className="  border-[1px] border-[#d2d8dd] bg-white p-2 px-6 rounded-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Clear
              </button>
            </div>
            <div className=" flex items-center gap-3  ">
              <button
                type="button"
                onClick={() => setShowAdvanced((s) => !s)}
                className="  text-xs min-w-[150px] sm:text-sm font-semibold border-[1px] p-2 rounded-xl"
              >
                {showAdvanced ? 'Hide advanced filters' : 'Show advanced filters'}
              </button>
              <p className="mt-1 text-xs sm:text-sm text-slate-500">Advanced filters cover date range, review state, follow-up, and owner.</p>
            </div>
          </div>

          {showAdvanced && (
            <div className="mt-4 space-y-4 rounded-2xl  bg-[#f8fafc] p-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                <div>
                  <label className={labelClass} htmlFor="tx-from">
                    From
                  </label>
                  <input
                    id="tx-from"
                    type="datetime-local"
                    className={inputClass}
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  />
                  <p className="mt-1 text-xs text-slate-500">UTC sent to API on apply.</p>
                </div>
                <div>
                  <label className={labelClass} htmlFor="tx-to">
                    To
                  </label>
                  <input
                    id="tx-to"
                    type="datetime-local"
                    className={inputClass}
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  />
                  <p className="mt-1 text-xs text-slate-500">UTC sent to API on apply.</p>
                </div>
                <div>
                  <label className={labelClass} htmlFor="tx-review">
                    Review state
                  </label>
                  <select id="tx-review" className={inputClass} value={reviewStateAdv} onChange={(e) => setReviewStateAdv(e.target.value)}>
                    
                    <option value="Needs Review">Needs Review</option>
                    <option value="Open">Open</option>
                    <option value="In review">In review</option>
                    <option value="Resolved">Resolved</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500">UI only — not sent to the API.</p>
                </div>
                <div>
                  <label className={labelClass} htmlFor="tx-follow">
                    Follow-up
                  </label>
                  <select id="tx-follow" className={inputClass} value={followUpAdv} onChange={(e) => setFollowUpAdv(e.target.value)}>
                   
<option value="Overdue">Overdue</option>
<option value="Reminder Enabled">Reminder Enabled</option>
<option value="Escalation Enabled">Escalation Enabled</option>
<option value="Reminder Due">Reminder Due</option>
<option value="Escalation Due">Escalation Due</option>
<option value="Escalated">Escalated</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500">UI only — not sent to the API.</p>
                </div>
                <div>
                  <label className={labelClass} htmlFor="tx-owners">
                    Owners
                  </label>
                  <select id="tx-owners" className={inputClass} value={ownersAdv} onChange={(e) => setOwnersAdv(e.target.value)}>
                    <option value="">All owners</option>
                    <option value="me">Me</option>
                    <option value="team">Team</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500">UI only — not sent to the API.</p>
                </div>
              </div>
              <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                Use owner and follow-up together when you want a clean action queue instead of scanning the whole table.
              </div>
            </div>
          )}
        </SurfaceCard>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}



        {/* Activity queue */}
        <SurfaceCard className="!p-0 overflow-hidden shadow-md">
          <div className="flex flex-col gap-0.5 border-b-[1px] border-slate-200/90 bg-[#F6F6F6] py-[18px] px-[30px] sm:flex-row sm:items-center sm:justify-between">
            
            <div className="">
              <h2 className="text-2xl font-semibold text-slate-900">Activity queue</h2>
              <p className="text-sm text-slate-500 mt-2">
                Filter by stage, account, and references — same server filters as before.
              </p>
            </div>
            <p className="text-sm text-slate-500">
              <span className="font-semibold text-slate-800">{visibleTransactions.length}</span> matching payments
            </p>
          </div>

          <div className="space-y-5  p-[30px]">
            {loading && transactions.length > 0 && (
              <div className="flex justify-center py-6">
                <Spinner animation="border" size="sm" style={{ color: BRAND }} />
              </div>
            )}
            {!loading &&
              visibleTransactions.map((transaction) => {
                const checked = selectedRowIds.includes(transaction.id);
                return (
                  <div
                    key={transaction.id}
                    className="rounded-xl border border-slate-200/90 p-4 shadow-sm transition-shadow hover:border-slate-300"
                    style={{ background: 'linear-gradient(180deg, rgba(34, 151, 209, 0.08) 0%, #F0F0F0 70%)' }}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between ">
                      <div className="flex min-w-0 flex-1 items-start gap-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => toggleRowSelected(transaction.id, e)}
                          className="mt-2 h-5 w-5 shrink-0 rounded border-slate-300 text-[#2297D1] focus:ring-[#2297D1]"
                          aria-label={`Select transaction ${transaction.id}`}
                        />
                        {/* <div className="mt-1.5 h-9 w-9 shrink-0 rounded-lg bg-slate-200" aria-hidden /> */}
                        <div className="min-w-0">
                          <p className="font-mono text-sm sm:text-base  font-bold tracking-tight text-slate-900">
                            {truncateAddress(transaction.uniqueWalletAddress)}
                          </p>
                          <p className="text-sm text-slate-500">Transaction #{transaction.id}</p>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                        <span
                          className={cn(
                            'inline-flex rounded-full px-3 py-1 text-xs font-semibold',
                            statusPillClass(transaction.status)
                          )}
                        >
                          {statusHeadline(transaction.status)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleTransactionClick(transaction)}
                          className="rounded-lg border border-[#2297D1] bg-white px-3 py-1.5 text-sm font-semibold text-[#2297D1] shadow-sm transition-colors hover:bg-sky-50"
                        >
                          View payment details
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      <div>
                        <p className="text-[10px] sm:text-base font-semibold uppercase tracking-[0.15em] text-[#4A4A4A]">Customer</p>
                        <p className="mt-1 text-sm sm:text-base font-semibold text-slate-900">{getUsernameById(transaction.userId)}</p>
                        <p className="text-xs text-slate-500">Account ID {transaction.userId}</p>
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-base font-semibold uppercase tracking-[0.15em] text-[#4A4A4A]">Amount</p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {formatAmount(transaction.fiatAmount, transaction.fiatCurrency)}
                        </p>
                        {(transaction.status === 'Completed' || transaction.status === 'PayoutCompleted') && (
                          <p className="text-xs text-slate-500">
                            Received {transaction.cryptoAmount} {transaction.cryptoCurrency}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-base font-semibold uppercase tracking-[0.15em] text-[#4A4A4A]">Review</p>
                        <span className="mt-1 inline-block rounded-xl   bg-[#F0F0F0] px-4 sm:py-2.5 py-1 text-xs font-semibold text-slate-800">
                          {reviewLabel(transaction.status)}
                        </span>
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-base font-semibold uppercase tracking-[0.15em] text-[#4A4A4A]">Updated</p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">{formatDate(transaction.timestamp)}</p>
                        <p className="text-xs text-slate-500">{new Date(transaction.timestamp).toLocaleTimeString()}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span className="font-semibold uppercase tracking-wide text-slate-400">Deposit wallet</span>
                      <span className="font-mono text-slate-700">{transaction.uniqueWalletAddress}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(transaction.uniqueWalletAddress).catch(() => {});
                        }}
                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 font-semibold text-[#2297D1] hover:bg-sky-50"
                      >
                        Copy
                      </button>
                    </div>
                    <p className="mt-3 text-sm text-slate-500">
                      Path: DirectCrypto — Provider: {transaction.provider || '—'}
                    </p>
                  </div>
                );
              })}
            {!loading && visibleTransactions.length === 0 && (
              <p className="py-8 text-center text-sm text-slate-500">No transactions found</p>
            )}
          </div>
          {pagination.totalPages > 1 && <div className="border-t py-3 border-slate-200/90 bg-white">{renderPagination()}</div>}
        </SurfaceCard>
      </div>

      <PaymentReviewModal
        show={showTransactionModal}
        onHide={handleCloseModal}
        transaction={selectedTransaction}
        userRole={userRole}
        walletBalances={walletBalances}
        loadingBalances={loadingBalances}
        manualSettling={manualSettlingId === selectedTransaction?.id}
        onRefresh={() => {
          void fetchTransactions(pagination.currentPage);
        }}
        onManualSettle={handleManualSettlement}
        getUsernameById={getUsernameById}
        getUserById={getUserById}
        getSuperAgentForAgent={getSuperAgentForAgent}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
      />
    </div>
  );
};

export default TransactionList;
